/* @flow */
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import ReactPopover from 'react-popover';
import defaultPopOvertheme from './defaultPopOverTheme.scss';
import StringUtils from 'common/utils/StringUtils';
import KeyEventHandler from 'common/components/core/util/KeyEventsHandler';
import { withObserver } from 'common/components/core/intersection_observer/ObserverFlag';
import isFunction from 'lodash/isFunction';

const DefaultPopOverInner = ({ popOverContent, theme, style }) => {
    return !StringUtils.isEmpty(popOverContent) ||
        isFunction(popOverContent) ? (
        <span className={classnames(theme.popUpInner)} style={style}>
            {isFunction(popOverContent) ? popOverContent() : popOverContent}
        </span>
    ) : null;
};

export const PopoverTriggerEnum = {
    Click: 'CLICK',
    Hover: 'HOVER'
};

export const PopoverPlaceEnum = {
    Above: 'above',
    Right: 'right',
    Below: 'below',
    Left: 'left',

    Row: 'row',
    Column: 'column',

    Start: 'start',
    End: 'end'
};

export type PopoverPlaceEnumType =
    | 'above'
    | 'right'
    | 'below'
    | 'left'
    | 'row'
    | 'column'
    | 'start'
    | 'end';

type Props = {
    popOverClassName?: string,
    preferPlace?: string,
    place?: string,
    popOverContent?: string | any,
    className?: string,
    'data-test-id'?: string,
    theme: Object,
    defaultIsOpen: boolean,
    popOverEndTimeOut?: ?number,
    popOverStartTimeOut?: ?number,
    isPopOverOpen?: boolean,
    smallPopover?: boolean,
    invertPopover?: boolean,
    triggerPopoverOn?: ?string,
    hidePopoverOnClick?: boolean,
    disableAnimation?: boolean,
    disableTransition?: boolean,
    enableAntialias?: boolean,
    disableArrow?: boolean,
    // hideOnOuterClick is added to prevent the poppover to close on clicking outside the popover content
    // This is to support the new ui upgrade popup where the popup has to close only on some actions
    // defined within the popover content
    hideOnOuterClick?: boolean,
    onClick?: () => void,
    onMouseEnter?: () => void,
    onMouseLeave?: () => void,
    onOuterAction?: () => void,
    onEscape?: () => void,
    onShow?: () => void,
    onHide?: () => void,
    appendTarget?: HTMLElement
};
type State = {
    isOpen: boolean
};

type Context = {
    appendTarget: ?HTMLElement
};

const PopoverContext = React.createContext<Context>({});

export const popOverFactory = ({
    Component,
    withObserverFlag,
    PopOverInner = DefaultPopOverInner
}) => {
    const PopOverInnerWithKeyEvents = KeyEventHandler(PopOverInner);

    class PopOver extends React.PureComponent<Props, State> {
        context: Context;

        constructor(props, context) {
            super(props, context);
            this.state = {
                isOpen: props.popOverStartTimeOut ? false : props.defaultIsOpen
            };
            if (props.popOverStartTimeOut) {
                this.timerStart = setTimeout(() => {
                    this.setState({
                        isOpen: true
                    });
                }, props.popOverStartTimeOut);
            }
            if (props.popOverEndTimeOut) {
                const startTimeOut = props.popOverStartTimeOut || 0;
                this.timerStop = setTimeout(() => {
                    this.setState({
                        isOpen: false
                    });
                }, startTimeOut + props.popOverEndTimeOut);
            }
        }

        componentWillUnmount() {
            clearTimeout(this.timerStart);
            clearTimeout(this.timerStop);
        }

        static contextType = PopoverContext;

        onClick = (...args) => {
            const {
                onClick,
                hidePopoverOnClick,
                triggerPopoverOn
            } = this.props;
            const { isOpen } = this.state;
            if (triggerPopoverOn === PopoverTriggerEnum.Click) {
                this.togglePopover();
            } else if (
                isOpen &&
                hidePopoverOnClick &&
                triggerPopoverOn === PopoverTriggerEnum.Hover
            ) {
                this.hidePopover();
            }
            onClick && onClick(...args);
        };
        onMouseEnter = (...args) => {
            const { onMouseEnter, triggerPopoverOn } = this.props;
            if (triggerPopoverOn === PopoverTriggerEnum.Hover) {
                this.showPopover();
            }
            onMouseEnter && onMouseEnter(...args);
        };
        onMouseLeave = (...args) => {
            const { onMouseLeave, triggerPopoverOn } = this.props;
            if (triggerPopoverOn === PopoverTriggerEnum.Hover) {
                this.hidePopover();
            }
            onMouseLeave && onMouseLeave(...args);
        };
        togglePopover = () => {
            if (this.state.isOpen) {
                this.hidePopover();
            } else {
                this.showPopover();
            }
        };
        hidePopover = () => {
            const { onHide } = this.props;
            this.setState({ isOpen: false });
            onHide && onHide();
        };
        showPopover = () => {
            const { onShow } = this.props;
            this.setState({ isOpen: true });
            onShow && onShow();
        };
        onOuterAction = () => {
            const {
                hideOnOuterClick,
                onOuterAction,
                triggerPopoverOn
            } = this.props;
            if (
                hideOnOuterClick &&
                triggerPopoverOn === PopoverTriggerEnum.Click
            ) {
                this.hidePopover();
            }
            onOuterAction && onOuterAction();
        };
        onEscape = () => {
            const { onEscape, triggerPopoverOn } = this.props;
            const { isOpen } = this.state;
            if (isOpen && triggerPopoverOn === PopoverTriggerEnum.Click) {
                this.hidePopover();
            }
            onEscape && onEscape();
        };
        render() {
            var {
                popOverClassName,
                preferPlace,
                place,
                popOverContent,
                className,
                theme,
                isPopOverOpen,
                smallPopover,
                invertPopover,
                triggerPopoverOn,
                disableAnimation,
                disableTransition,
                disableArrow,
                enableAntialias,
                isVisible,
                hidePopoverOnClick, //eslint-disable-line no-unused-vars
                testId,
                'data-test-id': dataTestId,
                maxPopoverWidth,
                ...others
            } = this.props;
            var body = null;
            let isOpen = isPopOverOpen;
            if (
                triggerPopoverOn === PopoverTriggerEnum.Click ||
                triggerPopoverOn === PopoverTriggerEnum.Hover
            ) {
                ({ isOpen } = this.state);
                if (
                    StringUtils.isEmpty(popOverContent) &&
                    !isFunction(popOverContent)
                ) {
                    isOpen = false;
                }
            }

            body = (
                <PopOverInnerWithKeyEvents
                    {...others}
                    style={{
                        maxWidth: maxPopoverWidth || undefined,
                        whiteSpace: maxPopoverWidth ? 'normal' : undefined
                    }}
                    onEscape={this.onEscape}
                    popOverContent={popOverContent}
                    smallPopover={smallPopover}
                    invertPopover={invertPopover}
                    theme={theme}
                    preferPlace={preferPlace}
                    place={place}
                    popOverClassName={popOverClassName}
                />
            );
            const extraProps = {};
            if (!others.appendTarget && this.context.appendTarget) {
                extraProps.appendTarget = this.context.appendTarget;
            }
            return (
                <ReactPopover
                    {...others}
                    {...extraProps}
                    disabled={false}
                    onOuterAction={this.onOuterAction}
                    body={body}
                    isOpen={isOpen && isVisible}
                    preferPlace={preferPlace}
                    place={place}
                    popOverContent={popOverContent}
                    className={classnames(
                        popOverClassName,
                        theme.popOverClassName,
                        {
                            [theme.smallPopover]: smallPopover,
                            [theme.invert]: invertPopover,
                            [theme.disableAnimation]: disableAnimation,
                            [theme.disableTransition]: disableTransition,
                            [theme.disableArrow]: disableArrow,
                            [theme.enableAntialias]: enableAntialias
                        }
                    )}
                >
                    {Component ? (
                        <Component
                            {...others}
                            testId={testId}
                            data-test-id={dataTestId}
                            theme={theme}
                            isPopOverOpen={isOpen && isVisible}
                            onClick={this.onClick}
                            onMouseEnter={this.onMouseEnter}
                            onMouseLeave={this.onMouseLeave}
                            className={className}
                        />
                    ) : this.props.children ? (
                        this.props.children({
                            onClick: this.onClick,
                            onMouseEnter: this.onMouseEnter,
                            onMouseLeave: this.onMouseLeave
                        })
                    ) : null}
                </ReactPopover>
            );
        }
    }
    PopOver.defaultProps = {
        invertPopover: true,
        disableAnimation: true,
        isVisible: true,
        defaultIsOpen: false,
        hideOnOuterClick: true
    };

    const PopOverComponent = withObserverFlag
        ? withObserver({
              threshold: 1
          })(PopOver)
        : PopOver;
    return themr('PopOver', defaultPopOvertheme, { withRef: true })(
        PopOverComponent
    );
};

const PopoverProvider = PopoverContext.Provider; // use this for changing append target

const Popover = popOverFactory({});
export { Popover, PopoverProvider };
export default popOverFactory;
